import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { restaurantWaiterApi, ValidateWaiterResponseDto } from '../../../services/restaurantApiService';
import salesService, { ValidateCashierResponseDto } from '../../../services/salesService';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../../config/brand';

// ✅ Giriş yapan kullanıcı bilgisi (Garson veya Kasiyer)
export interface RestaurantLoginInfo {
  // Ortak alanlar
  id: number;
  name: string;
  loginType: 'waiter' | 'cashier'; // ✅ Giriş tipi
  // Yetkiler
  canCloseSale: boolean;
  canCloseTable: boolean;
  canAddProduct: boolean;
  canRemoveProduct: boolean;
  canClearCart: boolean;
  canPrintToKitchen: boolean;
  canDeletePrintedItems: boolean;
  canDeleteUnprintedItems?: boolean; // Yazdırılmamış ürün silme yetkisi
  canTransferTable: boolean;
  canPosRefund?: boolean;
  canCancelReceipt?: boolean; // Adisyon iptal yetkisi
  canCancelUnprintedReceipt?: boolean; // Yazdırılmamış adisyon iptal yetkisi
  canPrintReceipt?: boolean; // Adisyon yazdırma yetkisi
  canAddProductAfterReceiptPrint?: boolean; // Adisyon sonrası ürün ekleme
  canUsePavoP2P?: boolean;
  // Kasiyer için ek yetkiler (sadece kasiyer girişinde dolu)
  canChangePrice?: boolean;
  canApplyDiscount?: boolean;
  canCancelSale?: boolean;
  canViewReports?: boolean;
  canViewEndOfDay?: boolean;
  canWarehouseOperations?: boolean;
  canViewAccountBalance?: boolean;
  canListAccounts?: boolean;
  canQuickAddItem?: boolean;
  canAddAccount?: boolean;
}

interface RestaurantLockScreenModalProps {
  open: boolean;
  restaurantScreenId: number;
  onUnlock: (loginInfo: RestaurantLoginInfo) => void;
}

const RestaurantLockScreenModal: React.FC<RestaurantLockScreenModalProps> = ({
  open,
  restaurantScreenId,
  onUnlock,
}) => {
  const { t } = useTranslation('salesSystem');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Numara butonu tıklama
  const handleNumberClick = (num: string) => {
    if (pin.length < 6) {
      setPin(pin + num);
      setError(null);
    }
  };

  // Backspace
  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
    setError(null);
  };

  // Clear (Temizle)
  const handleClear = () => {
    setPin('');
    setError(null);
  };

  // Enter - Şifre doğrulama (Önce garson, sonra kasiyer)
  const handleEnter = async () => {
    if (!pin) {
      setError(t('restaurantLockScreen.errors.enterPassword'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // ✅ 1. ÖNCE GARSON OLARAK DENE
      console.log('🔐 [RestaurantLockScreenModal] Garson olarak giriş deneniyor...');
      const waiterResponse: ValidateWaiterResponseDto = await restaurantWaiterApi.validatePin({
        RestaurantScreenId: restaurantScreenId,
        Pin: pin,
      });

      if (waiterResponse.IsValid && waiterResponse.WaiterId && waiterResponse.WaiterName) {
        // ✅ GARSON GİRİŞİ BAŞARILI
        const loginInfo: RestaurantLoginInfo = {
          id: waiterResponse.WaiterId,
          name: waiterResponse.WaiterName,
          loginType: 'waiter',
          canCloseSale: waiterResponse.CanCloseSale ?? false,
          canCloseTable: waiterResponse.CanCloseTable ?? false,
          canAddProduct: waiterResponse.CanAddProduct ?? false,
          canRemoveProduct: waiterResponse.CanRemoveProduct ?? false,
          canClearCart: waiterResponse.CanClearCart ?? false,
          canPrintToKitchen: waiterResponse.CanPrintToKitchen ?? false,
          canDeletePrintedItems: waiterResponse.CanDeletePrintedItems ?? false,
          canDeleteUnprintedItems: waiterResponse.CanDeleteUnprintedItems ?? false, // NULL ise kapalı
          canTransferTable: waiterResponse.CanTransferTable ?? false,
          canPosRefund: waiterResponse.CanPosRefund ?? false,
          canCancelReceipt: waiterResponse.CanCancelReceipt ?? false, // NULL ise kapalı
          canCancelUnprintedReceipt: waiterResponse.CanCancelUnprintedReceipt ?? false, // NULL ise kapalı
          canPrintReceipt: waiterResponse.CanPrintReceipt ?? false, // NULL ise kapalı
          canAddProductAfterReceiptPrint: waiterResponse.CanAddProductAfterReceiptPrint ?? false, // NULL ise kapalı
          canUsePavoP2P: waiterResponse.CanUsePavoP2P ?? false,
        };
        
        console.log('✅ [RestaurantLockScreenModal] GARSON girişi başarılı:', loginInfo.name);
        onUnlock(loginInfo);
        setPin('');
        return;
      }

      // ✅ 2. GARSON BAŞARISIZ - KASİYER OLARAK DENE
      console.log('🔐 [RestaurantLockScreenModal] Kasiyer olarak giriş deneniyor...');
      const cashierResponse: ValidateCashierResponseDto = await salesService.validateCashierPin({
        ScreenType: 'Restaurant',
        RestaurantScreenId: restaurantScreenId,
        Pin: pin,
      });

      if (cashierResponse.IsValid && cashierResponse.CashierId && cashierResponse.CashierName) {
        // ✅ KASİYER GİRİŞİ BAŞARILI
        const loginInfo: RestaurantLoginInfo = {
          id: cashierResponse.CashierId,
          name: cashierResponse.CashierName,
          loginType: 'cashier',
          // Restoran yetkileri
          canCloseSale: cashierResponse.CanCloseSale ?? false,
          canCloseTable: cashierResponse.CanCloseTable ?? false,
          canAddProduct: cashierResponse.CanAddProduct ?? false,
          canRemoveProduct: cashierResponse.CanRemoveProduct ?? false,
          canClearCart: cashierResponse.CanClearCart ?? false,
          canPrintToKitchen: cashierResponse.CanPrintToKitchen ?? false,
          canDeletePrintedItems: cashierResponse.CanDeletePrintedItems ?? false,
          canDeleteUnprintedItems: cashierResponse.CanDeleteUnprintedItems ?? false, // NULL ise kapalı
          canTransferTable: cashierResponse.CanTransferTable ?? false,
          canPosRefund: cashierResponse.CanPosRefund ?? false,
          canCancelReceipt: cashierResponse.CanCancelReceipt ?? false, // NULL ise kapalı
          canCancelUnprintedReceipt: cashierResponse.CanCancelUnprintedReceipt ?? false, // NULL ise kapalı
          canPrintReceipt: cashierResponse.CanPrintReceipt ?? false, // NULL ise kapalı
          canAddProductAfterReceiptPrint: cashierResponse.CanAddProductAfterReceiptPrint ?? false, // NULL ise kapalı
          canUsePavoP2P: cashierResponse.CanUsePavoP2P ?? false,
          // Kasiyer ek yetkileri
          canChangePrice: cashierResponse.CanChangePrice ?? false,
          canApplyDiscount: cashierResponse.CanApplyDiscount ?? false,
          canCancelSale: cashierResponse.CanCancelSale ?? false,
          canViewReports: cashierResponse.CanViewReports ?? false,
          canViewEndOfDay: cashierResponse.CanViewEndOfDay ?? false,
          canWarehouseOperations: cashierResponse.CanWarehouseOperations ?? false,
          canViewAccountBalance: cashierResponse.CanViewAccountBalance ?? false,
          canListAccounts: cashierResponse.CanListAccounts ?? false,
          canQuickAddItem: cashierResponse.CanQuickAddItem ?? false,
          canAddAccount: cashierResponse.CanAddAccount ?? false,
        };
        
        console.log('✅ [RestaurantLockScreenModal] KASİYER girişi başarılı:', loginInfo.name);
        onUnlock(loginInfo);
        setPin('');
        return;
      }

      // ✅ 3. HER İKİSİ DE BAŞARISIZ
      setError(t('restaurantLockScreen.errors.invalidPassword'));
      setPin('');
      
    } catch (err: any) {
      console.error('Şifre doğrulama hatası:', err);
      setError(t('restaurantLockScreen.errors.validationFailed'));
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Enter tuşu desteği
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEnter();
    } else if (e.key === 'Backspace') {
      handleBackspace();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={undefined} // ✅ Kapatma yok - sadece şifre ile açılır
      disableEscapeKeyDown // ✅ Escape tuşu devre dışı
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.92)', // Yarı saydam koyu arkaplan
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3,
        }}
        onKeyDown={handleKeyPress}
        tabIndex={0}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <img 
              src={BRAND.logos.header} 
              alt={`${BRAND.name} Logo`} 
              style={{ 
                width: '360px', // ✅ 2 kat büyütüldü (180px → 360px)
                height: 'auto'
              }} 
            />
          </Box>
        </Box>

        {/* PIN Input */}
        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            maxWidth: 450,
            width: '100%',
          }}
        >
          <TextField
            fullWidth
            type="password"
            value={pin}
            placeholder="••••••"
            InputProps={{
              readOnly: true,
              sx: {
                fontSize: '2rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                fontWeight: 'bold',
              },
            }}
            sx={{
              mb: 3,
              '& input': {
                textAlign: 'center',
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Numerik Keypad */}
          <Grid container spacing={1.5}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <Grid item xs={4} key={num}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => handleNumberClick(num.toString())}
                  disabled={loading}
                  sx={{
                    fontSize: '1.5rem',
                    height: 60,
                    fontWeight: 'bold',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  {num}
                </Button>
              </Grid>
            ))}

            {/* Alt satır: Clear, 0, Backspace */}
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleClear}
                disabled={loading}
                sx={{
                  fontSize: '0.9rem',
                  height: 60,
                  fontWeight: 'bold',
                  color: 'error.main',
                  borderColor: 'error.main',
                  '&:hover': { bgcolor: 'error.light', borderColor: 'error.dark' },
                }}
              >
                {t('restaurantLockScreen.clear')}
              </Button>
            </Grid>

            <Grid item xs={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleNumberClick('0')}
                disabled={loading}
                sx={{
                  fontSize: '1.5rem',
                  height: 60,
                  fontWeight: 'bold',
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                0
              </Button>
            </Grid>

            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleBackspace}
                disabled={loading}
                sx={{
                  fontSize: '1.2rem',
                  height: 60,
                  fontWeight: 'bold',
                }}
              >
                <BackspaceIcon />
              </Button>
            </Grid>
          </Grid>

          {/* Büyük Enter Butonu */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            color="success"
            onClick={handleEnter}
            disabled={loading || !pin}
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <LoginIcon />}
            sx={{
              mt: 3,
              height: 70,
              fontSize: '1.3rem',
              fontWeight: 'bold',
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' },
            }}
          >
            {loading ? t('restaurantLockScreen.validating') : t('restaurantLockScreen.login')}
          </Button>
          
          {/* ✅ Giriş tipi bilgisi */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              icon={<PersonIcon />} 
              label={t('restaurantLockScreen.waiter')}
              size="small" 
              variant="outlined" 
              sx={{ opacity: 0.7 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {t('restaurantLockScreen.or')}
            </Typography>
            <Chip 
              icon={<PointOfSaleIcon />} 
              label={t('restaurantLockScreen.cashier')}
              size="small" 
              variant="outlined"
              sx={{ opacity: 0.7 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            {t('restaurantLockScreen.footerHint')}
          </Typography>
        </Paper>
      </Box>
    </Dialog>
  );
};

export default RestaurantLockScreenModal;

